// Classroom / guided-build core (CLS-1..3, M37). PURE + deterministic (ENG-1): a
// ghost blueprint (a compiled STR-2 schematic pinned to a world origin), a cheap
// placed-vs-plan diff producing {correct,wrong,missing} counts, per-student
// attribution over an edit log (PLR-1/NET-6), a per-cell heat classification for
// the translucent overlay, and a small completion/unlock state machine (CLS-3).
// The host owns the render, the screen, and (in MP) feeding real per-student
// edits; every count here is reproducible on any peer. No new sim mechanics — this
// is entirely diff + overlay over shipped systems (schematics, multiplayer, edits).
import { CLS } from '../config';
import { schematicBounds, stampSchematic, type Schematic, type StateResolver } from '../gen/schematic-place';

export interface Vec3 { x: number; y: number; z: number; }

/** One planned cell: an expected block state at a world position. */
export interface PlanCell { readonly x: number; readonly y: number; readonly z: number; readonly state: number; }

/** A compiled ghost blueprint (CLS-1): a schematic resolved to expected states and
 *  pinned at a world origin/rotation, rendered as translucent phantom voxels — never
 *  real blocks. Air/KEEP cells are dropped: `cells` is exactly what a student must
 *  place. */
export interface Blueprint {
  readonly name: string;
  readonly origin: readonly [number, number, number];
  readonly rot: number;
  /** Expected non-air cells, in stamp order (deterministic). */
  readonly cells: readonly PlanCell[];
  readonly bounds: { minX: number; maxX: number; minY: number; maxY: number; minZ: number; maxZ: number };
}

/** Read a world block state at (x,y,z); the host passes the live world reader. */
export type CellRead = (x: number, y: number, z: number) => number;

/**
 * Compile a schematic into a blueprint pinned at `origin`, rotated `k`×90° CW.
 * Cells resolving to `air` (or '.'/KEEP, which `stampSchematic` never emits) are
 * dropped — only the blocks a student must place remain. Returns null past
 * CLS.MAX_CELLS (an oversized blueprint is refused, not stamped — the overlay + the
 * periodic diff stay bounded).
 */
export function compileBlueprint(
  schem: Schematic,
  origin: readonly [number, number, number],
  k: number,
  res: StateResolver,
  air: number,
): Blueprint | null {
  const cells: PlanCell[] = [];
  let over = false;
  stampSchematic(schem, origin, k, res, {
    set(x, y, z, state) {
      if (state === air) return; // air/KEEP = nothing to build here
      if (cells.length >= CLS.MAX_CELLS) { over = true; return; }
      cells.push({ x, y, z, state });
    },
  });
  if (over) return null;
  return {
    name: schem.name,
    origin,
    rot: ((k % 4) + 4) % 4,
    cells,
    bounds: schematicBounds(schem, origin, k),
  };
}

// ── Placed-vs-plan diff (CLS-2) ──────────────────────────────────────────────

export type CellStatus = 'correct' | 'wrong' | 'missing';

/** Classify one planned cell against the world — the heat overlay + the diff + the
 *  attribution all share this one rule (a different, non-air block in the way is
 *  `wrong`; still air is `missing`; an exact match is `correct`). */
export function classifyCell(cell: PlanCell, read: CellRead, air: number): CellStatus {
  const actual = read(cell.x, cell.y, cell.z);
  if (actual === cell.state) return 'correct';
  if (actual === air) return 'missing';
  return 'wrong';
}

export interface DiffCounts { correct: number; wrong: number; missing: number; total: number; }

/** The cheap periodic placed-vs-plan pass (CLS-2). `total` = the blueprint's block
 *  count, and `correct + wrong + missing === total`. */
export function diffBlueprint(bp: Blueprint, read: CellRead, air: number): DiffCounts {
  let correct = 0, wrong = 0, missing = 0;
  for (const c of bp.cells) {
    const s = classifyCell(c, read, air);
    if (s === 'correct') correct++;
    else if (s === 'wrong') wrong++;
    else missing++;
  }
  return { correct, wrong, missing, total: bp.cells.length };
}

/** 0..1 build progress (the correct fraction of the blueprint). */
export function progressOf(counts: DiffCounts): number {
  return counts.total === 0 ? 1 : counts.correct / counts.total;
}

/** Complete when every planned cell is correct (CLS-3). With the plan-cell diff this
 *  implies wrong = missing = 0. */
export function isComplete(counts: DiffCounts): boolean {
  return counts.total > 0 && counts.correct === counts.total;
}

/** Per-cell status for the heat overlay / ghost render (CLS-2). */
export interface GhostCell { readonly x: number; readonly y: number; readonly z: number; readonly status: CellStatus; }

export function heatmap(bp: Blueprint, read: CellRead, air: number): GhostCell[] {
  return bp.cells.map((c) => ({ x: c.x, y: c.y, z: c.z, status: classifyCell(c, read, air) }));
}

// ── Per-student attribution over an edit log (CLS-2, PLR-1/NET-6) ─────────────

/** A pure per-position edit log: who last touched the block at each cell. The host
 *  records every player edit (place/break) with the acting student's id; the diff
 *  then credits a correct/wrong cell to whoever last placed it (MP attribution). */
export class EditLog {
  private readonly by = new Map<string, string>();
  private static key(x: number, y: number, z: number): string { return `${x},${y},${z}`; }

  record(x: number, y: number, z: number, student: string): void { this.by.set(EditLog.key(x, y, z), student); }
  forget(x: number, y: number, z: number): void { this.by.delete(EditLog.key(x, y, z)); }
  attributedTo(x: number, y: number, z: number): string | null { return this.by.get(EditLog.key(x, y, z)) ?? null; }
  clear(): void { this.by.clear(); }
  get size(): number { return this.by.size; }
}

export interface StudentTally { student: string; correct: number; wrong: number; }

/** Per-student {correct,wrong} tallies, attributed via the edit log (CLS-2). A cell
 *  with no recorded editor (pre-existing / instructor-placed) is not credited to
 *  anyone. Sorted by student id for a stable display order. */
export function attributeProgress(bp: Blueprint, read: CellRead, air: number, log: EditLog): StudentTally[] {
  const tally = new Map<string, StudentTally>();
  const bump = (student: string, key: 'correct' | 'wrong'): void => {
    let t = tally.get(student);
    if (!t) { t = { student, correct: 0, wrong: 0 }; tally.set(student, t); }
    t[key]++;
  };
  for (const c of bp.cells) {
    const s = classifyCell(c, read, air);
    if (s === 'missing') continue;
    const who = log.attributedTo(c.x, c.y, c.z);
    if (who) bump(who, s === 'correct' ? 'correct' : 'wrong');
  }
  return [...tally.values()].sort((a, b) => (a.student < b.student ? -1 : a.student > b.student ? 1 : 0));
}

// ── Course / completion / unlock state machine (CLS-1/3) ─────────────────────

export interface BoardUpdate {
  counts: DiffCounts;
  complete: boolean;
  /** True only on the exact tick the active blueprint first reaches 100% — the host
   *  fires the completion toast (UI-7) once on this edge. */
  justCompleted: boolean;
  /** The blueprint unlocked by this completion (the next in the course), or null. */
  unlocked: Blueprint | null;
}

const EMPTY_COUNTS: DiffCounts = { correct: 0, wrong: 0, missing: 0, total: 0 };

/** The per-world classroom (CLS-1): an ordered course of blueprints, the active one,
 *  a per-blueprint completion latch (each fires once), and next-blueprint unlocking
 *  (CLS-3). */
export class ClassroomBoard {
  private course: Blueprint[] = [];
  private activeIndex = 0;
  private readonly completed = new Set<string>();

  /** Instructor places a single blueprint (replaces the course). */
  place(bp: Blueprint): void { this.setCourse([bp]); }

  /** Set an ordered course: the first is active, the rest unlock on completion. */
  setCourse(bps: readonly Blueprint[]): void {
    this.course = bps.slice();
    this.activeIndex = 0;
    this.completed.clear();
  }

  clear(): void { this.course = []; this.activeIndex = 0; this.completed.clear(); }

  active(): Blueprint | null { return this.course[this.activeIndex] ?? null; }
  get length(): number { return this.course.length; }
  /** Names of blueprints completed so far (unlock history). */
  get done(): readonly string[] { return [...this.completed]; }

  /** Run the diff for the active blueprint and advance the completion/unlock latch. */
  update(read: CellRead, air: number): BoardUpdate {
    const bp = this.active();
    if (!bp) return { counts: EMPTY_COUNTS, complete: false, justCompleted: false, unlocked: null };
    const counts = diffBlueprint(bp, read, air);
    const complete = isComplete(counts);
    let justCompleted = false;
    let unlocked: Blueprint | null = null;
    if (complete && !this.completed.has(bp.name)) {
      this.completed.add(bp.name);
      justCompleted = true;
      if (this.activeIndex + 1 < this.course.length) { this.activeIndex++; unlocked = this.active(); }
    }
    return { counts, complete, justCompleted, unlocked };
  }
}
